import { useCallback, useEffect, useState } from "react";

import {
  BLOCK_SIZE_RANGE,
  DEFAULT_SETTINGS,
  PEN_WIDTH_RANGE,
  type EditorSettings,
  type RegionEffect,
} from "../editor/types";

const STORAGE_KEY = "mosaic-studio:settings:v1";
export const SETTINGS_SCHEMA_VERSION = 4;

type Stored = Record<string, unknown>;

function number(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(min, Math.min(max, value))
    : fallback;
}

function boolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function oneOf<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

function color(value: unknown, fallback: string): string {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}

function readEffect(value: Stored): RegionEffect {
  const fallback = DEFAULT_SETTINGS.effect;
  return {
    maskStyle: oneOf(value.maskStyle, ["mosaic", "blur", "fill"], fallback.maskStyle),
    fillColor: color(value.fillColor, fallback.fillColor),
    blockSize: Math.round(
      number(value.blockSize, BLOCK_SIZE_RANGE.min, BLOCK_SIZE_RANGE.max, fallback.blockSize),
    ),
  };
}

export function loadSettings(): EditorSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const stored = JSON.parse(raw) as Stored;
    const version = typeof stored.schemaVersion === "number" ? stored.schemaVersion : 0;
    // Before v4 the effect lived at the top level and "autosave" downloaded a
    // file on every image switch by default, which surprised people.
    const effect =
      version >= 4 && stored.effect && typeof stored.effect === "object"
        ? readEffect(stored.effect as Stored)
        : readEffect(stored);
    return {
      ...DEFAULT_SETTINGS,
      // Tool choice is session-only: reopening always starts in selection.
      tool: DEFAULT_SETTINGS.tool,
      shapeKind: oneOf(stored.shapeKind, ["rect", "ellipse", "triangle"], DEFAULT_SETTINGS.shapeKind),
      effect,
      penWidth: Math.round(
        number(stored.penWidth, PEN_WIDTH_RANGE.min, PEN_WIDTH_RANGE.max, DEFAULT_SETTINGS.penWidth),
      ),
      showOutlines: boolean(stored.showOutlines, DEFAULT_SETTINGS.showOutlines),
      showGrid: boolean(stored.showGrid, DEFAULT_SETTINGS.showGrid),
      autoDownload: version >= 4 ? boolean(stored.autoDownload, false) : false,
      stripMetadata: boolean(stored.stripMetadata, DEFAULT_SETTINGS.stripMetadata),
      exportFormat: oneOf(
        stored.exportFormat,
        ["original", "png", "jpeg", "webp"],
        DEFAULT_SETTINGS.exportFormat,
      ),
      exportScale: number(stored.exportScale, 0.25, 2, DEFAULT_SETTINGS.exportScale),
      exportQuality: number(stored.exportQuality, 40, 100, DEFAULT_SETTINGS.exportQuality),
      exportRemoveBackground: boolean(
        stored.exportRemoveBackground,
        DEFAULT_SETTINGS.exportRemoveBackground,
      ),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function usePersistentSettings() {
  const [settings, setSettings] = useState<EditorSettings>(loadSettings);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      // Tool and preview describe the current session, not a preference.
      const { tool: _tool, preview: _preview, ...stored } = settings;
      void _tool;
      void _preview;
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...stored, schemaVersion: SETTINGS_SCHEMA_VERSION }),
        );
      } catch {
        // Private browsing or a full quota: settings simply do not persist.
      }
    }, 200);
    return () => window.clearTimeout(timer);
  }, [settings]);

  const updateSettings = useCallback(
    (patch: Partial<EditorSettings> | ((current: EditorSettings) => Partial<EditorSettings>)) => {
      setSettings((current) => ({
        ...current,
        ...(typeof patch === "function" ? patch(current) : patch),
      }));
    },
    [],
  );
  return [settings, updateSettings] as const;
}
